export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const DAY_KEYS: readonly DayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

export const ALL_DAYS: DayKey[] = [...DAY_KEYS];

export const DAY_LABELS: Record<DayKey, string> = {
  mon: "Mandag",
  tue: "Tirsdag",
  wed: "Onsdag",
  thu: "Torsdag",
  fri: "Fredag",
  sat: "Lørdag",
  sun: "Søndag",
};

export const DAY_LABELS_SHORT: Record<DayKey, string> = {
  mon: "Man",
  tue: "Tir",
  wed: "Ons",
  thu: "Tor",
  fri: "Fre",
  sat: "Lør",
  sun: "Søn",
};

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toUtcDate(d: Date): Date {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/**
 * ISO 8601 week number for the given date.
 * Returns { year, weekNumber } so cross-year edges are correct.
 */
export function isoWeekOf(date: Date): { year: number; weekNumber: number } {
  const d = toUtcDate(date);
  // ISO: Thursday of the week determines the year.
  const dayNr = (d.getUTCDay() + 6) % 7; // mon=0 .. sun=6
  d.setUTCDate(d.getUTCDate() - dayNr + 3);
  const firstThursday = Date.UTC(d.getUTCFullYear(), 0, 4);
  const firstThursdayDay = (new Date(firstThursday).getUTCDay() + 6) % 7;
  const week1Start = firstThursday - firstThursdayDay * 86400000;
  const weekNumber =
    Math.round((d.getTime() - week1Start) / (7 * 86400000)) + 1;
  return { year: d.getUTCFullYear(), weekNumber };
}

/** Monday (YYYY-MM-DD) of a given ISO week. */
export function isoWeekMonday(year: number, weekNumber: number): string {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // mon=0
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Dow);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (weekNumber - 1) * 7);
  return `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`;
}

/** YYYY-MM-DD for the day n days after the given YYYY-MM-DD. */
export function addDays(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function dayKeyFromIndex(index: number): DayKey {
  return DAY_KEYS[index] ?? "mon";
}

export function isDayKey(value: unknown): value is DayKey {
  return (
    typeof value === "string" && (DAY_KEYS as readonly string[]).includes(value)
  );
}

/** Returns today's DayKey if `today` falls inside the week starting at `monday`. */
export function todayDayKeyInRange(monday: string, today: Date): DayKey | null {
  const start = new Date(`${monday}T00:00:00Z`).getTime();
  const t = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const diffDays = Math.floor((t - start) / 86400000);
  if (diffDays < 0 || diffDays > 6) return null;
  return DAY_KEYS[diffDays] ?? null;
}

/** ISO year/week of the week immediately prior to (year, weekNumber). */
export function previousIsoWeek(
  year: number,
  weekNumber: number,
): { year: number; weekNumber: number } {
  const monday = isoWeekMonday(year, weekNumber);
  const prevMonday = addDays(monday, -7);
  const [y, m, d] = prevMonday.split("-").map(Number);
  return isoWeekOf(new Date(Date.UTC(y, m - 1, d)));
}

/** ISO year/week of the week immediately after (year, weekNumber). */
function nextIsoWeek(
  year: number,
  weekNumber: number,
): { year: number; weekNumber: number } {
  const monday = isoWeekMonday(year, weekNumber);
  const nextMonday = addDays(monday, 7);
  const [y, m, d] = nextMonday.split("-").map(Number);
  return isoWeekOf(new Date(Date.UTC(y, m - 1, d)));
}

/** YYYY-MM-DD for a Date, read in local time. */
function isoDateOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight for a YYYY-MM-DD, so `isoWeekOf` reads the intended day. */
function dateFromIsoDate(isoDate: string): Date {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** DayKey of a YYYY-MM-DD. */
function dayKeyOfIsoDate(isoDate: string): DayKey {
  const dt = new Date(`${isoDate}T00:00:00Z`);
  return dayKeyFromIndex((dt.getUTCDay() + 6) % 7);
}

/** One day a todo may be moved to. */
export interface MoveTarget {
  date: string;
  dayKey: DayKey;
  year: number;
  weekNumber: number;
  /** Set when the date is today or tomorrow, so the label can say so. */
  relative: "today" | "tomorrow" | null;
}

/** An ISO week, named the way a week record names itself. */
export interface IsoWeek {
  year: number;
  weekNumber: number;
}

/** Everything the day picker needs in order to draw the window. */
export interface MoveWindow {
  /** The days a todo may be moved to, in date order. */
  targets: MoveTarget[];
  /**
   * The day the card belongs to, when it falls inside the window's span.
   *
   * A bare date rather than a `MoveTarget`, deliberately: this day is drawn
   * and never moved to, and a string cannot be handed to anything that moves.
   */
  viewedDate: string | null;
}

/**
 * The **Flyttevindu** (docs/adr/0023): the days a todo may be moved to, from
 * today through the end of the furthest reachable week — or the end of the ISO
 * week after this one, whichever is later — minus the day the card belongs to.
 *
 * The start reads the wall clock. That is what lets a fortnight-old Wednesday
 * offer today first instead of a Thursday long past, and equally what lets
 * next Wednesday offer today at all — a window that runs in both directions.
 *
 * The end reads `reachableWeeks`, the open weeks still carrying this
 * huskeliste, so a week you went to the trouble of creating never falls
 * outside the window meant to reach it. Weeks inside the span that do not
 * exist yet are offered all the same; the confirm creates them.
 *
 * Never empty: the end is at least next Sunday, which is always later than
 * today, so a caller finding nothing to offer has found a bug.
 */
export function moveWindow(
  weekStartDate: string,
  dayKey: DayKey,
  today: Date,
  reachableWeeks: readonly IsoWeek[],
): MoveWindow {
  const viewed = addDays(weekStartDate, DAY_KEYS.indexOf(dayKey));
  const todayIso = isoDateOf(today);
  const tomorrowIso = addDays(todayIso, 1);
  const current = isoWeekOf(today);
  const next = nextIsoWeek(current.year, current.weekNumber);
  // ISO dates compare correctly as strings, so `later of` is a plain max.
  let end = addDays(isoWeekMonday(next.year, next.weekNumber), 6);
  for (const week of reachableWeeks) {
    const sunday = addDays(isoWeekMonday(week.year, week.weekNumber), 6);
    if (sunday > end) end = sunday;
  }

  const targets: MoveTarget[] = [];
  for (let date = todayIso; date <= end; date = addDays(date, 1)) {
    if (date === viewed) continue;
    const iso = isoWeekOf(dateFromIsoDate(date));
    targets.push({
      date,
      dayKey: dayKeyOfIsoDate(date),
      year: iso.year,
      weekNumber: iso.weekNumber,
      relative:
        date === todayIso ? "today" : date === tomorrowIso ? "tomorrow" : null,
    });
  }
  return {
    targets,
    viewedDate: viewed >= todayIso && viewed <= end ? viewed : null,
  };
}

const MOVE_TARGET_MONTH = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  timeZone: "UTC",
});

/** Enough of a day to name it — shared by a target and the day you stand on. */
type NamedDay = Pick<MoveTarget, "date" | "dayKey" | "relative">;

/** "I morgen · fredag 15. mai" / "Lørdag 16. mai" */
export function moveTargetLabel(target: NamedDay): string {
  const dayOfMonth = Number(target.date.slice(8, 10));
  const month = MOVE_TARGET_MONTH.format(new Date(`${target.date}T00:00:00Z`));
  const named = `${DAY_LABELS[target.dayKey]} ${dayOfMonth}. ${month}`;
  if (target.relative === "today") return `I dag · ${named.toLowerCase()}`;
  if (target.relative === "tomorrow") {
    return `I morgen · ${named.toLowerCase()}`;
  }
  return named;
}

/**
 * Short form for the quick-move button: "i dag", "i morgen", or the weekday.
 * Standing on a future day the window opens on neither of the first two, so
 * the button names the day rather than claiming a relation it does not have.
 */
export function moveTargetShortLabel(target: NamedDay): string {
  if (target.relative === "today") return "i dag";
  if (target.relative === "tomorrow") return "i morgen";
  return DAY_LABELS[target.dayKey].toLowerCase();
}

/**
 * One pill in the day picker.
 *
 * `target` is null for the day the card belongs to, drawn so the strip has no
 * hole where that day fell out and disabled because moving a todo to the day
 * it already sits on is not a move (docs/adr/0023).
 *
 * Not exported: nothing needs to name it, and a caller that does can reach it
 * as `MoveTargetGroup["days"][number]`.
 */
interface MoveDay {
  date: string;
  dayKey: DayKey;
  relative: "today" | "tomorrow" | null;
  target: MoveTarget | null;
}

export interface MoveTargetGroup {
  year: number;
  weekNumber: number;
  label: string;
  days: MoveDay[];
}

/**
 * The window split into one group per ISO week, in date order, with the day
 * the card belongs to drawn back in at the place it fell out of.
 *
 * Only the first two weeks are named for their relation to now: those are the
 * two you act on without thinking. Past them the week number is the honest
 * label — "om tre uker" is arithmetic the reader would have to trust.
 */
export function groupMoveTargets(
  span: MoveWindow,
  today: Date,
): MoveTargetGroup[] {
  const current = isoWeekOf(today);
  const nextWeek = nextIsoWeek(current.year, current.weekNumber);
  const todayIso = isoDateOf(today);
  const tomorrowIso = addDays(todayIso, 1);

  const days: MoveDay[] = span.targets.map((target) => ({
    date: target.date,
    dayKey: target.dayKey,
    relative: target.relative,
    target,
  }));
  if (span.viewedDate) {
    const date = span.viewedDate;
    days.push({
      date,
      dayKey: dayKeyOfIsoDate(date),
      relative:
        date === todayIso ? "today" : date === tomorrowIso ? "tomorrow" : null,
      target: null,
    });
    days.sort((a, b) => a.date.localeCompare(b.date));
  }

  const groups: MoveTargetGroup[] = [];
  for (const day of days) {
    const iso = isoWeekOf(dateFromIsoDate(day.date));
    const last = groups.at(-1);
    if (last?.year === iso.year && last.weekNumber === iso.weekNumber) {
      last.days.push(day);
      continue;
    }
    const isCurrent =
      iso.year === current.year && iso.weekNumber === current.weekNumber;
    const isNext =
      iso.year === nextWeek.year && iso.weekNumber === nextWeek.weekNumber;
    groups.push({
      year: iso.year,
      weekNumber: iso.weekNumber,
      label: isCurrent
        ? "Denne uken"
        : isNext
          ? `Neste uke · uke ${iso.weekNumber}`
          : `Uke ${iso.weekNumber}`,
      days: [day],
    });
  }
  return groups;
}

/** "5.–11. mai" style range label for a Monday YYYY-MM-DD. */
export function weekRangeLabel(monday: string): string {
  const [y, m, d] = monday.split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, d));
  const end = new Date(Date.UTC(y, m - 1, d + 6));
  const monthFmt = new Intl.DateTimeFormat("nb-NO", {
    month: "long",
    timeZone: "UTC",
  });
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  const startMonth = monthFmt.format(start);
  const endMonth = monthFmt.format(end);
  if (startMonth === endMonth) return `${startDay}.–${endDay}. ${endMonth}`;
  return `${startDay}. ${startMonth}–${endDay}. ${endMonth}`;
}
